import { FileProcessStatus } from '../enums/upload-status';

/**
 * 上传阶段结果
 */
export interface UploadResult {
    /** 服务器端文件标识 */
    fileId: string;
    /** 上传后的文件访问URL */
    fileUrl: string;
    /** 上传完成时间 */
    uploadTime: Date;
}

/**
 * 识别阶段结果
 */
export interface RecognitionResult {
    /** 识别的图片数量 */
    imageCount: number;
    /** 提取的文本内容 */
    extractedText?: string;
}

/**
 * 文件信息接口
 * 支持两阶段处理：上传 + 识别
 */
export interface FileInfo {
    /** 文件唯一标识 */
    id: string;
    /** 文件名 */
    name: string;
    /** 文件大小（字节） */
    size: number;
    /** 文件类型 */
    type: string;
    /** 文件内容类型 */
    contentType: 'DISCLOSURE' | 'CLAIM';
    /** 文件处理状态 */
    status: FileProcessStatus;
    /** 上传阶段进度 (0-100) */
    uploadProgress: number;
    /** 识别阶段进度 (0-100) */
    recognitionProgress: number;
    /** 上传阶段错误信息 */
    uploadError?: string;
    /** 识别阶段错误信息 */
    recognitionError?: string;
    /** 上传阶段结果 */
    uploadResult?: UploadResult;
    /** 识别阶段结果 */
    recognitionResult?: RecognitionResult;
    /** 原始文件对象（用于重试上传） */
    originalFile?: File;
}

/**
 * 第一阶段：文件上传服务接口
 */
export interface FileUploadService {
    /**
     * 上传文件到服务器
     * @param file 要上传的文件
     * @param onProgress 进度回调函数
     * @returns 上传结果
     */
    upload(file: File, onProgress?: (progress: number) => void): Promise<UploadResult>;
}

/**
 * 第二阶段：文件识别服务接口
 */
export interface FileRecognitionService {
    /**
     * 识别文件内容
     * @param fileId 服务器端文件ID
     * @param onProgress 进度回调函数
     * @returns 识别结果
     */
    recognize(fileId: string, onProgress?: (progress: number) => void): Promise<RecognitionResult>;
}

/**
 * 支持的文件类型
 */
export const SUPPORTED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;

/**
 * 文件类型映射
 */
export const FILE_TYPE_EXTENSIONS: Record<string, string[]> = {
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

/**
 * 最大文件大小限制 (10MB)
 */
export const MAX_FILE_SIZE = 10 * 1024 * 1024;
